import styled from "styled-components";

// ─── CSS reset scoped to settings ─────────────────────────────────────────────
export const SettingsGlobalStyle = `
  @keyframes settings-spin { to { transform: rotate(360deg) } }
  @keyframes settings-toast-in { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: translateY(0) } }
`;

// ─── Page shell ───────────────────────────────────────────────────────────────
// Every settings sub-page is wrapped in this.
// It provides the #f1f5f9 background and the standard font.
export const SettingsPage = styled.div`
  font-family: 'Segoe UI', system-ui, sans-serif;
  background: #f1f5f9;
  min-height: 100vh;
`;

// ─── Top bar ──────────────────────────────────────────────────────────────────
// White 56-px bar pinned to the top of each sub-page.
// Usage:
//   <SettingsTopBar>
//     <SettingsTopBarLeft>
//       <SettingsAccentBar />
//       <div>
//         <SettingsTopBarTitle>Page Title</SettingsTopBarTitle>
//         <SettingsTopBarSub>subtitle / meta</SettingsTopBarSub>
//       </div>
//     </SettingsTopBarLeft>
//     <SettingsTopBarRight>{/* buttons, dropdowns */}</SettingsTopBarRight>
//   </SettingsTopBar>
export const SettingsTopBar = styled.div`
  background: #fff;
  padding: 0 28px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

export const SettingsTopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// Teal-to-blue left accent stripe (matches EmissionKPI)
export const SettingsAccentBar = styled.div`
  width: 8px;
  height: 28px;
  border-radius: 4px;
  background: linear-gradient(to bottom, #3f88a5, #2563eb);
  flex-shrink: 0;
`;

export const SettingsTopBarTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
`;

export const SettingsTopBarSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.3;
`;

export const SettingsTopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// ─── Content area ─────────────────────────────────────────────────────────────
// Centred, padded content well beneath the top bar.
// maxWidth defaults to 1040 to match EmissionKPI; pass $wide for full-width tables.
export const SettingsContent = styled.div`
  padding: 24px;
  max-width: ${({ $wide }) => ($wide ? "100%" : "1040px")};
  margin: 0 auto;
`;

// ─── Filter / search card ─────────────────────────────────────────────────────
// White rounded card that holds search inputs and filter controls.
export const SettingsFilterCard = styled.div`
  background: #fff;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

// ─── Search input wrapper ─────────────────────────────────────────────────────
export const SettingsSearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;

  .s-icon {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 13px;
    color: #94a3b8;
    pointer-events: none;
  }

  input {
    width: 100%;
    padding: 9px 12px 9px 32px;
    border: 1.5px solid #e2e8f0;
    border-radius: 9px;
    font-size: 13px;
    outline: none;
    color: #1e293b;
    font-family: inherit;
    background: #fff;
    transition: border-color 0.15s;

    &::placeholder { color: #94a3b8; }
    &:focus { border-color: #93c5fd; }
  }
`;

// ─── Main data card ───────────────────────────────────────────────────────────
// White rounded card that wraps a table / list.
export const SettingsCard = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;
`;

// ─── Section header inside a card ─────────────────────────────────────────────
export const SettingsSectionHeader = styled.div`
  padding: 12px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SettingsSectionTitle = styled.span`
  font-size: 11px;
  font-weight: 800;
  color: #3f88a5;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

export const SettingsCountChip = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 20px;
  background: #e0f2fe;
  color: #0369a1;
  border: 1px solid #bae6fd;
`;

// ─── Status badge ─────────────────────────────────────────────────────────────
export const SettingsStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
  background: ${({ $active }) => ($active ? "#dcfce7" : "#fee2e2")};
  color: ${({ $active }) => ($active ? "#166534" : "#991b1b")};
  border: 1px solid ${({ $active }) => ($active ? "#bbf7d0" : "#fecaca")};
`;

// ─── Table primitives ─────────────────────────────────────────────────────────
export const SettingsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const SettingsTh = styled.th`
  padding: 10px 16px;
  font-size: 10px;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
  white-space: nowrap;
`;

export const SettingsTr = styled.tr`
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.1s;
  &:last-child { border-bottom: none; }
  &:hover { background: #f8fafc; }
`;

export const SettingsTd = styled.td`
  padding: 11px 16px;
  font-size: 13px;
  color: #1e293b;
  vertical-align: middle;
`;

// ─── Row actions cell ─────────────────────────────────────────────────────────
export const SettingsActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const SettingsActionBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 7px;
  border: 1px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.15s;
  flex-shrink: 0;

  ${({ $variant }) => {
        switch ($variant) {
            case "edit":
                return `border-color: #bae6fd; background: #f0f9ff; color: #0369a1;
                &:hover { background: #0369a1; color: #fff; border-color: #0369a1; }`;
            case "del":
                return `border-color: #fecaca; background: #fff5f5; color: #dc2626;
                &:hover { background: #dc2626; color: #fff; border-color: #dc2626; }`;
            case "view":
                return `border-color: #e2e8f0; background: #f8fafc; color: #64748b;
                &:hover { background: #3f88a5; color: #fff; border-color: #3f88a5; }`;
            default:
                return `border-color: #e2e8f0; background: #f8fafc; color: #64748b;
                &:hover { background: #475569; color: #fff; border-color: #475569; }`;
        }
    }}
`;

// ─── Empty state ──────────────────────────────────────────────────────────────
export const SettingsEmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  border: 1px dashed #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  .s-empty-icon {
    font-size: 28px;
    color: #cbd5e1;
    margin-bottom: 12px;
    display: block;
  }
  .s-empty-title {
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 6px;
  }
  .s-empty-sub {
    font-size: 12px;
    color: #94a3b8;
  }
`;

// ─── Modal form primitives ────────────────────────────────────────────────────
export const SettingsModalBody = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SettingsModalFooter = styled.div`
  padding: 12px 24px 16px;
  border-top: 0.5px solid rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const SettingsFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const SettingsFormLabel = styled.label`
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

export const SettingsFormInput = styled.input`
  width: 100%;
  border: 1.5px solid ${({ $accent }) => ($accent ? "#3f88a5" : "#e2e8f0")};
  border-radius: 9px;
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  color: #1e293b;
  background: ${({ $readonly }) => ($readonly ? "#f8fafc" : "#fff")};
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus:not([readOnly]) {
    border-color: #93c5fd;
    box-shadow: 0 0 0 3px rgba(63, 136, 165, 0.12);
  }
  &::placeholder { color: #94a3b8; }
`;

export const SettingsFormSelect = styled.select`
  width: 100%;
  border: 1.5px solid ${({ $accent }) => ($accent ? "#3f88a5" : "#e2e8f0")};
  border-radius: 9px;
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  color: ${({ value }) => (value ? "#1e293b" : "#94a3b8")};
  background: #fff;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2394a3b8'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 8px;
  padding-right: 28px;
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus {
    border-color: #93c5fd;
    box-shadow: 0 0 0 3px rgba(63, 136, 165, 0.12);
  }
`;

export const SettingsFormGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || "1fr 1fr"};
  gap: ${({ $gap }) => $gap || "12px"};
`;

// ─── Inline divider ───────────────────────────────────────────────────────────
export const SettingsDivider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: ${({ $my }) => $my || "8px 0"};
`;

// ─── Warning / info block inside modals ──────────────────────────────────────
export const SettingsWarningBlock = styled.div`
  padding: 12px 16px;
  border-radius: 9px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  font-size: 13px;
  color: #9a3412;
  line-height: 1.6;
`;

/**
 * Global Settings Layout Wrapper
 * Simplifies page creation by providing a standard shell.
 */
export const SettingLayout = ({ title, subtitle, rightContent, children, $wide }) => (
  <SettingsPage>
    <SettingsTopBar>
      <SettingsTopBarLeft>
        <SettingsAccentBar />
        <div>
          <SettingsTopBarTitle>{title}</SettingsTopBarTitle>
          <SettingsTopBarSub>{subtitle}</SettingsTopBarSub>
        </div>
      </SettingsTopBarLeft>
      <SettingsTopBarRight>{rightContent}</SettingsTopBarRight>
    </SettingsTopBar>
    <SettingsContent $wide={$wide}>
      {children}
    </SettingsContent>
  </SettingsPage>
);